import { gql } from '@apollo/client';
import { ConnectorType } from 'src/utils/gql/types';

const COMMON_METRICS_FIELDS = `
  category
  key
  label
  type
  id
  valueType
`;

const COMMON_METRICS_DATA_FIELDS = `
  key
  label
  metrics {
    ${COMMON_METRICS_FIELDS}
    category
    key
    label
    type
    id
    valueType
  }
`;

const COMPANY_FIELDS = `
  name
  industry
  companyDescription
  usageOptions
`;

export const SIGN_IN = gql`
  mutation signIn($signInData: SignInInput!) {
    signIn(signInData: $signInData)
  }
`;

export const COMPANY = gql`
  query company {
    company {
      ${COMPANY_FIELDS}
    }
  }
`;

export const GET_ALL_CUSTOM_FORMULAS = gql`
  query getAllCustomFormulas {
    getAllCustomFormulas {
      name
      id
      formulaData {
        label
        value
        type
        connectorType
      }
    }
  }
`;

export const GET_CUSTOM_FORMULA_BY_ID = gql`
  query getCustomFormulaById($id: String!) {
    getCustomFormulaById(id: $id) {
      name
      id
      formulaData {
        label
        value
        type
        connectorType
        metricId
        filters {
          key
          filterGroupOr {
            rulesAnd {
              operator
              values
            }
          }
        }
      }
      format {
        type
        description
        prefix
        suffix
        isDecimal
      }
    }
  }
`;

export const GET_FACEBOOK_METRICS = gql`
  query getFacebookMetrics {
    getFacebookMetrics {
      ${COMMON_METRICS_DATA_FIELDS}
    }
  }
`;

export const GET_GOOGLE_ADS_METRICS = gql`
  query getGoogleAdsMetricsList {
    getGoogleAdsMetricsList {
      ${COMMON_METRICS_DATA_FIELDS}
    }
  }
`;

export const GET_GOOGLE_ANALYTICS_METRICS = gql`
  query getGoogleAnalyticsMetrics {
    getGoogleAnalyticsMetrics {
      key
      label
      metrics {
        ${COMMON_METRICS_FIELDS}
        incompatibleMetrics
      }
    }
  }
`;

export const GET_GOOGLE_SEARCH_CONSOLE_METRICS = gql`
  query getGoogleSearchConsoleMetrics {
    getGoogleSearchConsoleMetrics {
      key
      label
      metrics {
        ${COMMON_METRICS_FIELDS}
      }
    }
  }
`;

export const GET_SHOPIFY_METRICS = gql`
  query getShopifyMetrics {
    getShopifyMetrics {
      ${COMMON_METRICS_DATA_FIELDS}
    }
  }
`;

export const GET_MAGENTO_METRICS = gql`
  query getMagentoMetrics {
    getMagentoMetrics {
      ${COMMON_METRICS_DATA_FIELDS}
    }
  }
`;

export const GET_GOOGLE_ADS_METRICS_DRAFT = gql`
  query getGoogleAdsMetrics(
    $args: MetricValuesArgs!
    $clientAccountId: String!
  ) {
    getGoogleAdsMetrics(args: $args, clientAccountId: $clientAccountId) {
      metricValues {
        value
        key
      }
      splitValues {
        key
        value
      }
    }
  }
`;

export const GET_UNIFIED_SEARCH_TERMS = gql`
  query getUnifiedSearchTerms(
    $customerId: String!
    $dateFrom: String!
    $dateTo: String!
  ) {
    getUnifiedSearchTerms(
      customerId: $customerId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      date
      campaignId
      campaignName
      campaignType
      adGroupId
      adGroupName
      searchTerm
      impressions
      clicks
      costMicros
      sourceView
    }
  }
`;

export const GET_SEARCH_SHOPPING_TERMS = gql`
  query getSearchShoppingTerms(
    $customerId: String!
    $dateFrom: String!
    $dateTo: String!
  ) {
    getSearchShoppingTerms(
      customerId: $customerId
      dateFrom: $dateFrom
      dateTo: $dateTo
    )
  }
`;

export const GET_PERFORMANCE_MAX_TERMS = gql`
  query getPerformanceMaxTerms(
    $customerId: String!
    $dateFrom: String!
    $dateTo: String!
  ) {
    getPerformanceMaxTerms(
      customerId: $customerId
      dateFrom: $dateFrom
      dateTo: $dateTo
    )
  }
`;

export const GET_SHOPIFY_FILTERS = gql`
  query getShopifyFilters {
    getShopifyFilters {
      availableOperators {
        label
        key
      }
      key
      expectedReturnValueType
    }
  }
`;

export const GET_MAGENTO_FILTERS = gql`
  query getMagentoFilters {
    getMagentoFilters {
      availableOperators {
        label
        key
      }
      key
      expectedReturnValueType
    }
  }
`;

export const GET_SHOPIFY_METRICS_VALUES = gql`
  query getShopifyMetricValues($args: MetricValuesArgs!) {
    getShopifyMetricValues(args: $args) {
      filtersOptions {
        key
        values
      }
      metricRows {
        metricValues {
          value
          key
        }
        splitValues {
          key
          value
        }
      }
    }
  }
`;

export const GET_MAGENTO_METRICS_VALUES = gql`
  query getMagentoMetricValues($args: MetricValuesArgs!) {
    getMagentoMetricValues(args: $args) {
      filtersOptions {
        key
        values
      }
      metricRows {
        metricValues {
          value
          key
        }
        splitValues {
          key
          value
        }
      }
    }
  }
`;

export const GET_GOOGLE_ANALYTICS_4_METRICS = gql`
  query getGoogleAnalytics4Metrics(
    $args: MetricValuesArgs!
    $propertyId: String!
  ) {
    getGoogleAnalytics4Metrics(args: $args, propertyId: $propertyId) {
      splitValues {
        key
        value
      }
      metricValues {
        key
        value
      }
    }
  }
`;

export const GET_GOOGLE_SEARCH_CONSOLE_METRIC_VALUES = gql`
  query getGoogleSearchConsoleMetricValues($args: MetricValuesArgs!) {
    getGoogleSearchConsoleMetricValues(args: $args) {
      splitValues {
        key
        value
      }
      metricValues {
        key
        value
      }
    }
  }
`;

export const GET_FACEBOOK_METRICS_VALUES = gql`
  query getFacebookMetricsValues($args: MetricValuesArgs!) {
    getFacebookMetricsValues(args: $args) {
      splitValues {
        key
        value
      }
      metricValues {
        key
        value
      }
    }
  }
`;

export const GET_KIPIFY_METRICS_VALUES = gql`
  query getKipifyMetricsValues($args: MetricValuesArgs!) {
    getKipifyMetricsValues(args: $args) {
      splitValues {
        key
        value
      }
      metricValues {
        key
        value
      }
    }
  }
`;

export const ME = gql`
  query me {
    me {
      lastName
      firstName
      email
      id
      role
      databaseConnectorEnabled
      shopifyConnectorEnabled
      magentoConnectorEnabled
      deckStudioEnabled
      company {
        name
      }
    }
  }
`;

export const GENERATE_DECK = gql`
  mutation generateDeck($input: GenerateDeckInput!) {
    generateDeck(input: $input) {
      success
      message
      deckUrl
      responseId
      deckTokensUsed
      deckCreditsRemaining
      rawResponse
    }
  }
`;

export const GET_FACEBOOK_AUTH_URL = gql`
  query getFacebookAuthUrl {
    getFacebookAuthUrl
  }
`;

export const GET_SHOPIFY_AUTH_URL = gql`
  query getShopifyAuthUrl($shop: String!) {
    getShopifyAuthUrl(shop: $shop)
  }
`;

export const GET_GOOGLE_ADS_AUTH_URL = gql`
  query getGoogleAdsAuthUrl {
    getGoogleAdsAuthUrl
  }
`;

export const GET_GOOGLE_ANALYTICS_AUTH_URL = gql`
  query getAnalyticsAuthUrl {
    getAnalyticsAuthUrl
  }
`;

export const GET_GOOGLE_SEARCH_CONSOLE_AUTH_URL = gql`
  query getGoogleSearchConsoleAuthUrl {
    getGoogleSearchConsoleAuthUrl
  }
`;

export const GET_CONNECTORS = gql`
  query getConnectors($type: ConnectorType) {
    getConnectors(type: $type) {
      id
      connectorType
      displayName
    }
  }
`;

export const GET_CONNECTOR_SELECTIONS = gql`
  query getConnectorSelections {
    getConnectorSelections {
      connectorId
      connectorType
      selectedAccountIds
      selectedSubaccountIds
    }
  }
`;

export const GET_AVAILABLE_FILTERS_FOR_METRIC = gql`
  query getAvailableFiltersForMetric($metricId: String!) {
    getAvailableFiltersForMetric(metricId: $metricId) {
      type
      operators {
        key
        label
      }
      label
      key
      id
      expectedReturnValueType
      shouldAvailableValuesBeRequested
      availableValues {
        key
        label
      }
    }
  }
`;

export const GET_FILTERS = gql`
  query getAvailableFilters($connectorType: ConnectorType!) {
    getFilters(connectorType: $connectorType) {
      id
      label
      key
      type
      expectedReturnValueType
      shouldAvailableValuesBeRequested
      operators {
        key
        label
      }
      availableValues {
        key
        label
      }
    }
  }
`;

export const GET_FILTER_AVAILABLE_VALUES = gql`
  query getFilterAvailableValues(
    $filterKey: String!
    $connectorType: ConnectorType!
  ) {
    getFilterAvailableValues(
      filterKey: $filterKey
      connectorType: $connectorType
    )
  }
`;

export const GET_SPLITS = gql`
  query getSplits($connectorType: ConnectorType) {
    getSplits(connectorType: $connectorType) {
      id
      connectorType
      key
      label
      valueType
    }
  }
`;

export const CHECK_CONNECTOR_TOKEN_VALIDITY = gql`
  query checkConnectorTokenValidity($connectorId: String!) {
    checkConnectorTokenValidity(connectorId: $connectorId)
  }
`;

export const createGetAccountsQuery = (ids: string[], type: ConnectorType) => {
  const queries = ids
    .map((id, i) => {
      switch (type) {
        case ConnectorType.GOOGlE_ANALYTICS4:
          return `
              getAccounts${i}: getAnalyticsAccounts(connectorId: "${id}") {
                id
                name
                selected
                properties {
                  id
                  name
                  selected
                }
              }
            `;

        case ConnectorType.GOOGLE_SEARCH_CONSOLE:
          return `
              getAccounts${i}: getGoogleSearchConsoleSites(connectorId: "${id}") {
                id
                name
                selected
              }
            `;

        case ConnectorType.GOOGLE_ADS:
          return `
              getAccounts${i}: getGoogleAdsAccounts(connectorId: "${id}") {
                id
                selected
                subaccounts {
                  id
                  name
                  selected
                }
              }
            `;

        case ConnectorType.FACEBOOK:
          return `
            getAccounts${i}: getFacebookAdAccounts(connectorId: "${id}") {
              id
              name
              selected
            }
          `;

        default:
          return '';
      }
    })
    .join('\n');

  return gql`
    query getAccounts {
      ${queries}
    }
  `;
};

export const PLACEHOLDER_QUERY = gql`
  query {
    __typename
  }
`;

export const GET_CUSTOM_FORMULAS = gql`
  query getAllCustomFormulas {
    getAllCustomFormulas {
      id
      name
      formulaData {
        label
        value
        type
        connectorType
      }
      format {
        type
        description
        prefix
        suffix
        isDecimal
      }
    }
  }
`;

export const GET_CUSTOM_FORMULA_VALUE = gql`
  query getCustomFormulaValues($args: CustomFormulaMetricValuesArgs!) {
    getCustomFormulaValues(args: $args) {
      metricValues {
        key
        value
      }
      splitValues {
        key
        value
      }
    }
  }
`;

export const GET_CUSTOM_REPORTS = gql`
  query getCustomReports {
    getCustomReports {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const GET_KPI_SECTIONS = gql`
  query getKpiSections {
    getKpiSections {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

export const GET_DATABASE_METRICS_FOR_CUSTOM_REPORT = gql`
  query getDatabaseConnectorMetricsForCustomReport($connectorId: String!) {
    getDatabaseConnectorMetricsForCustomReport(connectorId: $connectorId) {
      key
      name
      type
    }
  }
`;

export const GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT = gql`
  query getDatabaseConnectorSplitsForCustomReport($connectorId: String!) {
    getDatabaseConnectorSplitsForCustomReport(connectorId: $connectorId) {
      key
      name
      type
    }
  }
`;

export const GET_DATABASE_DATA_FOR_CUSTOM_REPORT = gql`
  query getDatabaseConnectorDataForCustomReport(
    $connectorId: String!
    $splits: [String!]
    $metrics: [String!]
    $filters: [String!]
    $dateRange: DatabaseDateRangeInput
  ) {
    getDatabaseConnectorDataForCustomReport(
      connectorId: $connectorId
      splits: $splits
      metrics: $metrics
      filters: $filters
      dateRange: $dateRange
    ) {
      metricValues {
        key
        value
      }
      splitValues {
        key
        value
      }
    }
  }
`;

export const GET_DB_CONNECTOR_CONFIG = gql`
  query GetDatabaseConnectorConfig($connectorId: String!) {
    getDatabaseConnectorConfig(connectorId: $connectorId) {
      dateColumn
      selectedTables
      selectedSplits
      selectedMetrics
    }
  }
`;

export const GET_DATABASE_JOIN_GRAPH = gql`
  query getDatabaseConnectorJoinGraph($connectorId: String!) {
    getDatabaseConnectorJoinGraph(connectorId: $connectorId) {
      tableA
      tableB
    }
  }
`;

export const SAVE_DB_CONNECTOR_CONFIG = gql`
  mutation SaveDatabaseConnectorConfig($input: SaveDbConnectorConfigInput!) {
    saveDatabaseConnectorConfig(input: $input) {
      success
    }
  }
`;

export const INTROSPECT_TABLES = gql`
  query IntrospectTables($connectionId: String!) {
    introspectTables(connectionId: $connectionId) {
      schema
      tableName
    }
  }
`;

export const INTROSPECT_COLUMNS = gql`
  query IntrospectColumns(
    $connectionId: String!
    $schema: String!
    $table: String!
  ) {
    introspectColumns(
      connectionId: $connectionId
      schema: $schema
      table: $table
    ) {
      columnName
      dataType
      isNullable
    }
  }
`;

export const GET_SPLITS_AND_METRICS = gql`
  query GetSplitsAndMetrics(
    $connectionId: String!
    $schema: String!
    $table: String!
  ) {
    getSplitsAndMetrics(
      connectionId: $connectionId
      schema: $schema
      table: $table
    ) {
      splits {
        fieldName
        dataType
        isNullable
        category
        reason
      }
      metrics {
        fieldName
        dataType
        isNullable
        category
        reason
      }
    }
  }
`;

export const GET_JOIN_SUGGESTIONS = gql`
  query GetJoinSuggestions(
    $connectionId: String!
    $mainTable: String!
    $schema: String!
  ) {
    getJoinSuggestions(
      connectionId: $connectionId
      mainTable: $mainTable
      schema: $schema
    ) {
      mainTable
      joins {
        table
        on
        type
        description
      }
      suggestedSplits
      suggestedMetrics
    }
  }
`;

export const PREVIEW_DATA = gql`
  query PreviewData(
    $connectionId: String!
    $schema: String!
    $table: String!
    $fields: [String!]!
    $limit: Int!
  ) {
    previewData(
      connectionId: $connectionId
      schema: $schema
      table: $table
      fields: $fields
      limit: $limit
    ) {
      data
    }
  }
`;
// Shopify sync status for top bar
export const GET_SHOPIFY_SYNC_STATUS = gql`
  query GetShopifySyncStatus {
    getShopifySyncStatus {
      status
      startedAt
      endedAt
      progressPercentage
    }
  }
`;

export const GET_MAGENTO_SYNC_STATUS = gql`
  query GetMagentoSyncStatus {
    getMagentoSyncStatus {
      status
      startedAt
      endedAt
    }
  }
`;
